import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar,
  Clock,
  TreePalm,
  IndianRupee,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  User,
  Building2,
  MapPin,
  Briefcase,
  ShieldCheck,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Bell,
  Receipt,
  Plane,
  Eye,
  EyeOff,
  UserCheck,
  CalendarDays,
  Plus,
  Megaphone,
  TrendingUp,
  Timer,
} from 'lucide-react';
import { ESS_NAVIGATION } from '../constants/essNavigation';
import { CompanyNewsTicker } from './CompanyNewsTicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('present') || s.includes('approved') || s.includes('confirmed') || s.includes('released')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (s.includes('pending') || s.includes('submitted') || s.includes('late')) {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (s.includes('absent') || s.includes('rejected') || s.includes('missing')) {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

// ============================================================================
// 1. EMPLOYEE DASHBOARD SUMMARY (For /dashboard — Simple, Fast, At-a-Glance)
// ============================================================================
export const EmployeeDashboardSummary = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [maskedPay, setMaskedPay] = useState(true);

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const fetchSummaryData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/ess/dashboard`, { withCredentials: true });
      if (res.data && res.data.success) {
        setData(res.data);
      } else {
        setError('Failed to load dashboard summary data.');
      }
    } catch (err) {
      console.error('Error loading ESS dashboard summary:', err);
      setError(err.response?.data?.detail || err.message || 'Unable to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="ess-summary-skeleton" className="space-y-6 max-w-7xl mx-auto">
        <div className="bg-slate-100 animate-pulse rounded-2xl p-6 h-36"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-slate-100 animate-pulse rounded-xl p-5 h-36 border border-slate-200"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="ess-summary-error" className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center max-w-xl mx-auto my-12 space-y-4">
        <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
        <h3 className="text-lg font-bold text-rose-900">Unable to load dashboard</h3>
        <p className="text-sm text-rose-700">{error}</p>
        <button
          onClick={fetchSummaryData}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm transition shadow-xs"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  const emp = data?.employee || {};
  const attendance = data?.attendance || {};
  const leave = data?.leave || {};
  const holidays = data?.holidays || {};
  const payroll = data?.payroll || {};
  const pendingActions = data?.pending_actions || [];
  const requests = data?.requests || [];
  const notifications = data?.notifications || {};
  const recentActivity = data?.recent_activity || [];

  return (
    <div data-testid="employee-dashboard-summary" className="space-y-6 max-w-7xl mx-auto pb-8 transition-all duration-300">
      {/* HEADER: Employee Identity Snapshot */}
      <div className="relative bg-gradient-to-r from-[#0052FF] via-[#1E64FF] to-[#3B82F6] rounded-2xl p-6 md:p-7 text-white shadow-md overflow-hidden">
        <div className="absolute -right-12 -top-12 w-64 h-64 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {emp.profile_photo ? (
                <img src={emp.profile_photo} alt={emp.name} className="w-14 h-14 rounded-2xl object-cover border-2 border-white/40 shadow-sm" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md border-2 border-white/40 flex items-center justify-center text-white text-xl font-bold shadow-sm">
                  {emp.name ? emp.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() : 'EMP'}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 border-2 border-white rounded-full"></div>
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 text-white backdrop-blur-xs">
                  {emp.code !== 'N/A' ? `Emp ID: ${emp.code}` : 'Employee Home'}
                </span>
                <span className="text-xs text-blue-100 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> {emp.current_date}
                </span>
              </div>
              <h1 className="text-xl md:text-2xl font-bold mt-1 tracking-tight">
                {emp.greeting || `Welcome, ${emp.name}`}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-y-1 gap-x-3 text-xs text-blue-100">
                {emp.designation && emp.designation !== 'N/A' && (
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 opacity-80" /> {emp.designation}</span>
                )}
                {emp.department && emp.department !== 'N/A' && (
                  <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5 opacity-80" /> {emp.department}</span>
                )}
                {emp.company && emp.company !== 'N/A' && (
                  <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5 opacity-80" /> {emp.company}</span>
                )}
                {emp.reporting_manager && emp.reporting_manager !== 'N/A' && (
                  <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 opacity-80" /> Mgr: {emp.reporting_manager}</span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Profile Badge */}
          <div className="shrink-0 bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/20 min-w-[180px]">
            <div className="flex items-center justify-between text-xs mb-1 font-medium">
              <span className="text-blue-100">Profile Progress</span>
              <span className="font-bold text-white">{emp.profile_completion_percentage || 0}%</span>
            </div>
            <div className="w-full bg-black/20 rounded-full h-1.5 overflow-hidden">
              <div className="bg-emerald-400 h-full rounded-full transition-all duration-500" style={{ width: `${emp.profile_completion_percentage || 0}%` }}></div>
            </div>
            <button
              onClick={() => navigate('/ess/portal')}
              className="mt-2.5 text-[11px] font-semibold text-white hover:text-blue-200 flex items-center justify-between w-full group transition"
            >
              <span>Open My Portal Workspace</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* COMPANY BROADCAST NEWS TICKER */}
      <CompanyNewsTicker initialAnnouncements={data?.announcements || []} hideWhenEmpty={true} />

      {/* ALERTS / ACTION REQUIRED BANNER */}
      {pendingActions.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg shrink-0 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">
                Action Required: {pendingActions[0].title}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">{pendingActions[0].description}</p>
              {pendingActions[0].completed_sections && pendingActions[0].completed_sections.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-[10px] uppercase font-bold text-slate-500">Completed:</span>
                  {pendingActions[0].completed_sections.map((sec) => (
                    <span key={sec} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      ✓ {sec}
                    </span>
                  ))}
                  {pendingActions[0].pending_sections && pendingActions[0].pending_sections.length > 0 && (
                    <>
                      <span className="text-[10px] uppercase font-bold text-slate-500 ml-1">Pending:</span>
                      {pendingActions[0].pending_sections.map((sec) => (
                        <span key={sec} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                          ⏳ {sec}
                        </span>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(pendingActions[0].path || '/ess/portal')}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold transition shrink-0 shadow-xs"
          >
            Take Action →
          </button>
        </div>
      )}

      {/* 6 SUMMARY METRIC CARDS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-summary-cards">
        {/* Card 1: Today's Attendance */}
        <div data-testid="summary-card-attendance" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Today's Attendance</span>
              <div className="p-2 rounded-lg bg-blue-50 text-[#0052FF]">
                <Clock className="w-5 h-5" />
              </div>
            </div>
            <div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusBadge(attendance.today_status)}`}>
                <span className="w-2 h-2 rounded-full bg-current"></span>
                {attendance.today_status || 'Not Checked In'}
              </span>
            </div>
            <div className="mt-3 text-xs text-slate-600 space-y-1">
              <p>In: <span className="font-bold text-slate-800">{attendance.check_in || 'Not recorded'}</span> {attendance.check_out ? `| Out: ${attendance.check_out}` : ''}</p>
              <p className="text-slate-500">{attendance.current_month || 'This Month'}: <span className="font-bold text-slate-800">{attendance.present_days || 0} Days Present</span></p>
            </div>
          </div>
          <button
            onClick={() => navigate('/ess/portal')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-[#0052FF] hover:text-blue-700 flex items-center justify-between w-full transition"
          >
            <span>View Attendance Details</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Card 2: Leave Balance Summary */}
        <div data-testid="summary-card-leave" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Leave Balance</span>
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <TreePalm className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xl font-bold text-slate-900">
              {leave.summary?.total_available || 0} <span className="text-xs font-normal text-slate-500">Days Available</span>
            </p>
            <div className="mt-2 space-y-1">
              {leave.balances && leave.balances.length > 0 ? (
                leave.balances.slice(0, 2).map((lb, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate max-w-[120px]">{lb.leave_type_name}</span>
                    <span className="font-bold text-slate-800">{lb.available} <span className="text-[10px] text-slate-400 font-normal">/ {lb.entitled}</span></span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400">No active leave balances</p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/ess/portal')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center justify-between w-full transition"
          >
            <span>View Leave Balances</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Card 3: Upcoming Holiday Summary */}
        <div data-testid="summary-card-holidays" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upcoming Holiday</span>
              <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
                <Calendar className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xl font-bold text-slate-900">
              {holidays.upcoming_count || 0} <span className="text-xs font-normal text-slate-500">remaining this year</span>
            </p>
            <div className="mt-2 space-y-1">
              {holidays.upcoming_list && holidays.upcoming_list.length > 0 ? (
                <div className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-slate-800 font-semibold truncate max-w-[140px]">{holidays.upcoming_list[0].name}</span>
                  <span className="text-purple-700 font-bold bg-purple-50 px-2 py-0.5 rounded text-[11px]">
                    {holidays.upcoming_list[0].date_formatted || holidays.upcoming_list[0].date}
                  </span>
                </div>
              ) : (
                <p className="text-xs text-slate-400">No upcoming holidays</p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/ess/portal')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center justify-between w-full transition"
          >
            <span>View Holiday List</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Card 4: Latest Payroll Summary */}
        <div data-testid="summary-card-payroll" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Latest Payroll</span>
              <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                <IndianRupee className="w-5 h-5" />
              </div>
            </div>
            {payroll.processed ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 font-medium">{payroll.latest_month}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${statusBadge(payroll.status)}`}>
                    {payroll.status}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-slate-500">Net Salary</span>
                  <div className="flex items-center gap-1">
                    <span className="text-base font-bold text-slate-900 font-mono">
                      {maskedPay ? '₹ ••••••' : payroll.net_pay}
                    </span>
                    <button onClick={() => setMaskedPay(!maskedPay)} className="text-slate-400 hover:text-slate-600 p-0.5">
                      {maskedPay ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400">Payroll not processed yet</p>
            )}
          </div>
          <button
            onClick={() => navigate('/ess/pay-tax')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center justify-between w-full transition"
          >
            <span>View Payslip & Tax</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Card 5: Pending Requests Summary */}
        <div data-testid="summary-card-requests" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Requests</span>
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                <FileText className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xl font-bold text-slate-900">
              {requests.filter(r => (r.status || '').toLowerCase().includes('pending')).length}{' '}
              <span className="text-xs font-normal text-slate-500">active requests in review</span>
            </p>
            <div className="mt-2 space-y-1">
              {requests.length > 0 ? (
                <p className="text-xs text-slate-600 truncate">Latest: {requests[0].type} ({requests[0].status})</p>
              ) : (
                <p className="text-xs text-slate-400">No active pending requests</p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/ess/portal')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center justify-between w-full transition"
          >
            <span>View All Requests</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Card 6: Notifications Summary */}
        <div data-testid="summary-card-notifications" className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notifications</span>
              <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
                <Bell className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xl font-bold text-slate-900">
              {notifications.unread_count || 0} <span className="text-xs font-normal text-slate-500">unread updates</span>
            </p>
            <div className="mt-2 space-y-1">
              {notifications.items && notifications.items.length > 0 ? (
                <p className="text-xs text-slate-600 truncate">{notifications.items[0].title}</p>
              ) : (
                <p className="text-xs text-slate-400">No unread notifications</p>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/notifications')}
            className="mt-4 pt-3 border-t border-slate-100 text-xs font-semibold text-rose-600 hover:text-rose-700 flex items-center justify-between w-full transition"
          >
            <span>View All Notifications</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* SHORT RECENT ACTIVITY SECTION */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <h3 className="text-base font-bold text-slate-900">Recent Activity Summary</h3>
          </div>
          <button
            onClick={() => navigate('/ess/portal')}
            className="text-xs font-semibold text-[#0052FF] hover:underline flex items-center gap-1"
          >
            View Workspace Overview <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="space-y-2">
          {recentActivity && recentActivity.length > 0 ? (
            recentActivity.slice(0, 4).map((act, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
                <div>
                  <p className="font-medium text-slate-800">{act.activity}</p>
                  <p className="text-[10px] text-slate-400">{act.timestamp}</p>
                </div>
                <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold border ${statusBadge(act.status)}`}>
                  {act.status}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-slate-400 py-4 text-center">No recent activity logged</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 2. EMPLOYEE WORKSPACE OVERVIEW (For My Portal → Overview — Detailed Workspace)
// ============================================================================
export const EmployeeWorkspaceOverview = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [maskedPay, setMaskedPay] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/ess/overview`, { withCredentials: true });
      if (res.data && res.data.success) {
        setData(res.data);
      } else {
        setError('Failed to load employee workspace data.');
      }
    } catch (err) {
      console.error('Error loading ESS overview:', err);
      setError(err.response?.data?.detail || err.message || 'Unable to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="ess-overview-skeleton" className="space-y-6 max-w-7xl mx-auto">
        <div className="bg-slate-100 animate-pulse rounded-2xl p-6 h-40"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-slate-100 animate-pulse rounded-xl p-5 h-28 border border-slate-200"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="ess-overview-error" className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center max-w-xl mx-auto my-12 space-y-4">
        <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
        <h3 className="text-lg font-bold text-rose-900">Unable to load workspace</h3>
        <p className="text-sm text-rose-700">{error}</p>
        <button
          onClick={fetchOverviewData}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm transition shadow-xs"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  const emp = data?.employee || {};
  const attendance = data?.attendance || {};
  const leave = data?.leave || {};
  const permissions = data?.permissions || {};
  const holidays = data?.holidays || {};
  const payroll = data?.payroll || {};
  const pendingActions = data?.pending_actions || [];
  const requests = data?.requests || [];
  const travel = data?.travel || {};
  const expenses = data?.expenses || {};
  const documents = data?.documents || {};
  const announcements = data?.announcements || [];

  return (
    <div data-testid="employee-workspace-overview" className="space-y-6 max-w-7xl mx-auto pb-12 transition-all duration-300">
      {/* 1. EMPLOYEE WORKSPACE PROFILE CARD */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {emp.profile_photo ? (
                <img src={emp.profile_photo} alt={emp.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-slate-100 shadow-xs" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 border-2 border-indigo-100 flex items-center justify-center text-indigo-700 text-xl font-bold shadow-xs">
                  {emp.name ? emp.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() : 'EMP'}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {emp.code !== 'N/A' ? `Emp ID: ${emp.code}` : 'Employee Workspace'}
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {emp.current_date}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mt-1 tracking-tight">
                {emp.name}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-slate-600">
                {emp.designation && emp.designation !== 'N/A' && (
                  <span className="flex items-center gap-1 font-medium"><Briefcase className="w-3.5 h-3.5 text-slate-400" /> {emp.designation}</span>
                )}
                {emp.department && emp.department !== 'N/A' && (
                  <span className="flex items-center gap-1 font-medium"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {emp.department}</span>
                )}
                {emp.company && emp.company !== 'N/A' && (
                  <span className="flex items-center gap-1 font-medium"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {emp.company}</span>
                )}
                {emp.reporting_manager && emp.reporting_manager !== 'N/A' && (
                  <span className="flex items-center gap-1 font-medium"><UserCheck className="w-3.5 h-3.5 text-slate-400" /> Manager: {emp.reporting_manager}</span>
                )}
              </div>
            </div>
          </div>

          <div className="shrink-0 bg-slate-50 rounded-xl p-4 border border-slate-200 min-w-[200px]">
            <div className="flex items-center justify-between text-xs mb-1.5 font-semibold text-slate-700">
              <span>Profile Completion</span>
              <span className="font-bold text-indigo-600">{emp.profile_completion_percentage || 0}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className="bg-indigo-600 h-full rounded-full transition-all duration-500" style={{ width: `${emp.profile_completion_percentage || 0}%` }}></div>
            </div>
            <button
              onClick={() => navigate('/employee-profile')}
              className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center justify-between w-full"
            >
              <span>{emp.profile_completion_percentage < 100 ? 'Complete Profile' : 'View Full Profile'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* COMPANY BROADCAST NEWS TICKER */}
      <CompanyNewsTicker initialAnnouncements={announcements} hideWhenEmpty={true} />

      {/* 2. QUICK ACTIONS (Retained under Overview as Detailed Workspace Tools) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs" data-testid="ess-quick-actions-section">
        <h2 className="text-base font-bold text-slate-900 mb-3">Quick Workspace Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Apply Leave', path: '/ess/leave-attendance', icon: TreePalm, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
            { label: 'Regularization', path: '/ess/leave-attendance', icon: Clock, color: 'bg-blue-50 text-blue-600 border-blue-200' },
            { label: 'Permission', path: '/ess/leave-attendance', icon: Timer, color: 'bg-purple-50 text-purple-600 border-purple-200' },
            { label: 'Local Conveyance', path: '/ess/local-travel', icon: MapPin, color: 'bg-orange-50 text-orange-600 border-orange-200' },
            { label: 'Travel Request', path: '/travel/dashboard', icon: Plane, color: 'bg-sky-50 text-sky-600 border-sky-200' },
            { label: 'Expense Claim', path: '/ess/expense-claims', icon: Receipt, color: 'bg-teal-50 text-teal-600 border-teal-200' },
            { label: 'Pay & Payslips', path: '/ess/pay-tax', icon: IndianRupee, color: 'bg-amber-50 text-amber-600 border-amber-200' },
            { label: 'My Documents', path: '/ess/kyc', icon: ShieldCheck, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
          ].map((act, i) => (
            <button
              key={i}
              onClick={() => navigate(act.path)}
              className="p-3.5 rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-xs transition bg-slate-50/50 hover:bg-white flex flex-col items-center text-center gap-2 group"
            >
              <div className={`p-2.5 rounded-xl border ${act.color} group-hover:scale-105 transition-transform`}>
                <act.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold text-slate-800">{act.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 3. ROW 1: DETAILED ATTENDANCE & DETAILED LEAVE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Detailed Overview */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between" data-testid="overview-attendance-section">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Attendance Overview</h3>
                <p className="text-xs text-slate-500">{attendance.current_month || 'Current Month'}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${statusBadge(attendance.today_status)}`}>
                Today: {attendance.today_status || 'Not Checked In'}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-center">
              <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                <p className="text-2xl font-bold text-blue-700">{attendance.present_days || 0}</p>
                <p className="text-xs font-medium text-blue-600 mt-0.5">Present</p>
              </div>
              <div className="bg-rose-50/60 rounded-xl p-3 border border-rose-100">
                <p className="text-2xl font-bold text-rose-700">{attendance.absent_days || 0}</p>
                <p className="text-xs font-medium text-rose-600 mt-0.5">Absent</p>
              </div>
              <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-100">
                <p className="text-2xl font-bold text-emerald-700">{attendance.leave_days || 0}</p>
                <p className="text-xs font-medium text-emerald-600 mt-0.5">Leave</p>
              </div>
              <div className="bg-teal-50/60 rounded-xl p-3 border border-teal-100" data-testid="dashboard-payable-days-card">
                <p className="text-2xl font-bold text-teal-700">{attendance.total_payable_days ?? attendance.payable_days ?? attendance.present_days ?? 0}</p>
                <p className="text-xs font-medium text-teal-600 mt-0.5">Payable Days</p>
              </div>
              <div className="bg-amber-50/60 rounded-xl p-3 border border-amber-100 col-span-2 sm:col-span-1">
                <p className="text-2xl font-bold text-amber-700">{attendance.late_days || 0}</p>
                <p className="text-xs font-medium text-amber-600 mt-0.5">Late</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-2 text-center text-xs text-slate-600">
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Working Days</span>
                <span className="font-bold text-slate-800">{attendance.working_days || 0}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Holidays</span>
                <span className="font-bold text-slate-800">{attendance.holidays_count || 0}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Weekly Off</span>
                <span className="font-bold text-slate-800">{attendance.weekly_offs || 0}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">Pending Regs</span>
                <span className="font-bold text-amber-600">{attendance.pending_regularizations || 0}</span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 text-right">
            <button
              onClick={() => navigate('/ess/leave-attendance')}
              className="text-xs font-semibold text-[#0052FF] hover:underline inline-flex items-center gap-1"
            >
              View Full Attendance Calendar <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Detailed Leave Balances Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between" data-testid="overview-leave-section">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">My Leave Balances</h3>
                <p className="text-xs text-slate-500">Policy entitlements & live balance</p>
              </div>
              <button
                onClick={() => navigate('/ess/leave-attendance')}
                className="text-xs font-semibold text-emerald-600 hover:underline inline-flex items-center gap-1"
              >
                Apply Leave <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-3">
              {leave.balances && leave.balances.length > 0 ? (
                leave.balances.map((lb, idx) => {
                  const pct = lb.entitled > 0 ? Math.min(100, Math.round((lb.used / lb.entitled) * 100)) : 0;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-800">{lb.leave_type_name} ({lb.leave_type_code})</span>
                        <span className="font-semibold text-slate-600">
                          <span className="text-emerald-600 font-bold">{lb.available}</span> Available / {lb.entitled} Entitled
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
                        <div className="bg-emerald-500 h-full" style={{ width: `${Math.max(0, 100 - pct)}%` }}></div>
                        <div className="bg-amber-400 h-full" style={{ width: `${pct}%` }}></div>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400">
                        <span>Used: {lb.used} day(s)</span>
                        {lb.pending > 0 && <span className="text-amber-600 font-medium">Pending: {lb.pending} day(s)</span>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 text-slate-400 text-xs">
                  No leave balances assigned for your category.
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={() => navigate('/ess/leave-attendance')}
              className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition"
            >
              Apply Leave
            </button>
            <button
              onClick={() => navigate('/ess/leave-attendance')}
              className="text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              View Leave History →
            </button>
          </div>
        </div>
      </div>

      {/* 4. ROW 2: PERMISSIONS WORKSPACE & MY REQUESTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Permissions Workspace Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between" data-testid="overview-permissions-section">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Permission Usage & Policy</h3>
                <p className="text-xs text-slate-500">Monthly limit from Policy Master</p>
              </div>
              <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-full">
                Max {permissions.max_duration_label || '1 Hour'} / Request
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <div className="bg-purple-50/60 rounded-xl p-3 border border-purple-100">
                <p className="text-2xl font-bold text-purple-700">{permissions.monthly_limit ?? 1}</p>
                <p className="text-xs font-medium text-purple-600 mt-0.5">Monthly Limit</p>
              </div>
              <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-100">
                <p className="text-2xl font-bold text-emerald-700">{permissions.remaining_count ?? 1}</p>
                <p className="text-xs font-medium text-emerald-600 mt-0.5">Remaining</p>
              </div>
              <div className="bg-amber-50/60 rounded-xl p-3 border border-amber-100">
                <p className="text-2xl font-bold text-amber-700">{permissions.used_count ?? 0}</p>
                <p className="text-xs font-medium text-amber-600 mt-0.5">Used Approved</p>
              </div>
            </div>

            <div className="space-y-2 text-xs text-slate-600 bg-slate-50 rounded-xl p-3 border border-slate-100">
              <div className="flex justify-between">
                <span>Pending Approval:</span>
                <span className="font-bold text-amber-600">{permissions.pending_count ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Approved This Month:</span>
                <span className="font-bold text-emerald-600">{permissions.approved_count ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Policy Name:</span>
                <span className="font-bold text-slate-800">{permissions.policy_name || 'Standard Policy'}</span>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 text-right">
            <button
              onClick={() => navigate('/ess/leave-attendance')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold transition"
            >
              Apply Permission →
            </button>
          </div>
        </div>

        {/* My Requests & Claims Section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between" data-testid="overview-requests-section">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">My Requests & Claims Status</h3>
                <p className="text-xs text-slate-500">Submitted items & approval tracking</p>
              </div>
              <span className="text-xs font-semibold text-slate-500">Recent 5</span>
            </div>

            {requests && requests.length > 0 ? (
              <div className="space-y-2">
                {requests.slice(0, 5).map((req, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-200 bg-slate-50/50 hover:bg-white transition text-xs">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-50 text-[#0052FF] shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{req.type}</p>
                        <p className="text-[11px] text-slate-400">{req.date} &middot; {req.amount}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${statusBadge(req.status)}`}>
                        {req.status}
                      </span>
                      <p className="text-[10px] text-slate-400 mt-0.5">{req.approval_stage}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 text-xs bg-slate-50 rounded-xl border border-dashed border-slate-200">
                No active requests logged.
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 text-right">
            <button
              onClick={() => navigate('/ess/portal')}
              className="text-xs font-semibold text-[#0052FF] hover:underline"
            >
              View My Requests Inbox →
            </button>
          </div>
        </div>
      </div>

      {/* 5. ROW 3: HOLIDAY CALENDAR & PAYROLL WORKSPACE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Holidays Workspace */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs" data-testid="overview-holidays-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">Upcoming Company Holidays</h3>
              <p className="text-xs text-slate-500">Configured Master Holiday Calendar</p>
            </div>
            <button
              onClick={() => setShowHolidayModal(true)}
              className="text-xs font-semibold text-purple-600 hover:underline flex items-center gap-1"
            >
              Full Calendar ({holidays.total_count || 0}) →
            </button>
          </div>

          {holidays.upcoming_list && holidays.upcoming_list.length > 0 ? (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
              {holidays.upcoming_list.slice(0, 4).map((h, i) => (
                <div key={i} className="flex items-center justify-between p-3 hover:bg-slate-50 transition text-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-700 flex flex-col items-center justify-center shrink-0 border border-purple-100">
                      <span className="font-bold leading-none">{h.date_formatted ? h.date_formatted.split(' ')[0] : ''}</span>
                      <span className="text-[9px] uppercase font-semibold leading-none mt-0.5">{h.date_formatted ? h.date_formatted.split(' ')[1] : ''}</span>
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{h.name}</p>
                      <p className="text-[11px] text-slate-400">{h.day}</p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${h.is_optional ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
                    {h.type}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-xs bg-slate-50 rounded-xl border border-dashed border-slate-200">
              No upcoming holidays remaining.
            </div>
          )}
        </div>

        {/* Payroll & Payslips Workspace */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between" data-testid="overview-payroll-section">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Latest Payroll & Payslip</h3>
                <p className="text-xs text-slate-500">Processed salary releases & history</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                <IndianRupee className="w-5 h-5" />
              </div>
            </div>

            {payroll.processed ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase font-semibold">Payroll Period</span>
                    <span className="font-bold text-slate-900">{payroll.latest_month}</span>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full font-bold text-[11px] border ${statusBadge(payroll.status)}`}>
                    {payroll.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="text-slate-500 block text-[10px] uppercase font-semibold">Gross Salary</span>
                    <span className="font-bold text-slate-900 font-mono text-sm">{payroll.gross_pay}</span>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50/50 border border-amber-100">
                    <div className="flex items-center justify-between">
                      <span className="text-amber-800 block text-[10px] uppercase font-semibold">Net Pay</span>
                      <button onClick={() => setMaskedPay(!maskedPay)} className="text-amber-600 hover:text-amber-800">
                        {maskedPay ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <span className="font-bold text-amber-900 font-mono text-sm">{maskedPay ? '₹ ••••••' : payroll.net_pay}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 text-xs bg-slate-50 rounded-xl border border-dashed border-slate-200">
                Payroll not processed yet for this period.
              </div>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 text-right">
            <button
              onClick={() => navigate('/ess/pay-tax')}
              className="text-xs font-semibold text-amber-600 hover:underline inline-flex items-center gap-1"
            >
              View Full Payslip & Tax Projections <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* 6. ROW 4: TRAVEL/CONVEYANCE, EXPENSES, DOCUMENTS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Travel & Conveyance */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Travel & Conveyance</span>
              <div className="p-2 rounded-lg bg-orange-50 text-orange-600">
                <Plane className="w-5 h-5" />
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                <span className="text-slate-600">Pending Travel Requests</span>
                <span className="font-bold text-slate-900">{travel.pending_travel_requests || 0}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                <span className="text-slate-600">Pending Local Conveyance</span>
                <span className="font-bold text-slate-900">{travel.pending_local_conveyance || 0}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
            <button onClick={() => navigate('/ess/local-travel')} className="font-semibold text-orange-600 hover:underline">
              Local Conveyance
            </button>
            <button onClick={() => navigate('/travel/dashboard')} className="font-semibold text-slate-700 hover:underline">
              Travel Desk →
            </button>
          </div>
        </div>

        {/* Expenses & Reimbursements */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Expenses & Claims</span>
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <Receipt className="w-5 h-5" />
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                <span className="text-slate-600">Pending Approval</span>
                <span className="font-bold text-amber-600">{expenses.pending_approval || 0}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                <span className="text-slate-600">Pending Amount</span>
                <span className="font-bold text-slate-900">₹{expenses.total_pending_amount || 0}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
            <button onClick={() => navigate('/ess/expense-claims')} className="font-semibold text-emerald-600 hover:underline">
              Submit Claim
            </button>
            <button onClick={() => navigate('/ess/conveyance-ledger')} className="font-semibold text-slate-700 hover:underline">
              Claims Ledger →
            </button>
          </div>
        </div>

        {/* Documents & KYC */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Documents & KYC</span>
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                <ShieldCheck className="w-5 h-5" />
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                <span className="text-slate-600">KYC Status</span>
                <span className={`px-2 py-0.5 rounded font-bold text-[11px] border ${statusBadge(documents.kyc_status)}`}>
                  {documents.kyc_status || 'Not Started'}
                </span>
              </div>
              {documents.missing_items && documents.missing_items.length > 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
                  Missing: {documents.missing_items.join(', ')}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
            <button onClick={() => navigate('/ess/kyc')} className="font-semibold text-indigo-600 hover:underline">
              KYC Verification
            </button>
            <button onClick={() => navigate('/ess/assets-docs')} className="font-semibold text-slate-700 hover:underline">
              My Assets & Docs →
            </button>
          </div>
        </div>
      </div>

      {/* HOLIDAY MODAL */}
      {showHolidayModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-xl space-y-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between border-b pb-3 border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Holiday Calendar</h3>
                <p className="text-xs text-slate-500">Applicable company holidays for current year ({holidays.total_count || 0} total)</p>
              </div>
              <button onClick={() => setShowHolidayModal(false)} className="text-slate-400 hover:text-slate-600 text-lg font-bold">
                ✕
              </button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {((holidays.all_list && holidays.all_list.length > 0) ? holidays.all_list : (holidays.upcoming_list || [])).length > 0 ? (
                ((holidays.all_list && holidays.all_list.length > 0) ? holidays.all_list : (holidays.upcoming_list || [])).map((h, idx) => {
                  const todayStr = new Date().toISOString().split('T')[0];
                  const isPast = h.date < todayStr;
                  return (
                    <div key={idx} className={`flex items-center justify-between p-3 rounded-xl border ${isPast ? 'bg-slate-50/80 border-slate-100 opacity-75' : 'bg-white border-purple-100 hover:bg-purple-50/40'} transition text-xs`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-slate-900">{h.name}</p>
                          {isPast && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold">Completed</span>}
                        </div>
                        <p className="text-slate-500 text-[11px] mt-0.5">{h.day} &middot; {h.date_formatted || h.date}</p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full font-bold text-[11px] ${h.is_optional ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
                        {h.type}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-center py-8 text-slate-400 text-xs">No holidays configured for this calendar year.</p>
              )}
            </div>
            <div className="pt-3 border-t border-slate-100 text-right">
              <button onClick={() => setShowHolidayModal(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const EmployeeDashboardView = EmployeeDashboardSummary;
export default EmployeeDashboardSummary;
